/** Fixed report theme — org branding colors are no longer used in PDF chrome. */
export const REPORT_PRIMARY_COLOR = "#4F7BD9";
export const REPORT_PRIMARY_TEXT_COLOR = "#FFFFFF";
export const REPORT_BODY_TEXT_COLOR = "#1F2937";
export const REPORT_BORDER_COLOR = "#E5E7EB";
export const REPORT_ROW_ALT_COLOR = "#F6F8FC";

const DEFAULT_PRIMARY_COLOR = REPORT_PRIMARY_COLOR;

const normalizeHexColor = (hexColor, fallback = DEFAULT_PRIMARY_COLOR) => {
    if (!hexColor || typeof hexColor !== "string") {
        return fallback;
    }

    const normalized = hexColor.trim().replace("#", "");

    if (/^[0-9a-fA-F]{6}$/.test(normalized)) {
        return `#${normalized}`;
    }

    return fallback;
};

const getContrastTextColor = (hexColor, lightColor = "#FFFFFF", darkColor = "#000000") => {
    const normalized = normalizeHexColor(hexColor).replace("#", "");
    const red = parseInt(normalized.slice(0, 2), 16);
    const green = parseInt(normalized.slice(2, 4), 16);
    const blue = parseInt(normalized.slice(4, 6), 16);
    const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;

    return luminance > 0.55 ? darkColor : lightColor;
};

const generateColor = (hexColor, magnitude) => {
    const normalized = normalizeHexColor(hexColor).replace("#", "");

    const decimalColor = parseInt(normalized, 16);
    let r = (decimalColor >> 16) + magnitude;
    r > 255 && (r = 255);
    r < 0 && (r = 0);
    let g = (decimalColor & 0x0000ff) + magnitude;
    g > 255 && (g = 255);
    g < 0 && (g = 0);
    let b = ((decimalColor >> 8) & 0x00ff) + magnitude;
    b > 255 && (b = 255);
    b < 0 && (b = 0);

    return `#${(g | (b << 8) | (r << 16)).toString(16).padStart(6, "0")}`;
};

/**
 * Split table rows into page-sized chunks so each chunk can render with
 * wrap={false} (no mid-table split) and its own frozen header.
 */
export const chunkTableRows = (rows = [], chunkSize = 18) => {
    const safeRows = Array.isArray(rows) ? rows : [];
    const size = Math.max(1, chunkSize);

    if (!safeRows.length) {
        return [[]];
    }

    const chunks = [];

    for (let index = 0; index < safeRows.length; index += size) {
        chunks.push(safeRows.slice(index, index + size));
    }

    return chunks;
};

const formatDisplayValue = (value, fallback = "-") => {
    if (value === null || value === undefined || value === "") {
        return fallback;
    }

    if (Array.isArray(value)) {
        const formattedItems = value
            .map((item) => formatDisplayValue(item, ""))
            .filter(Boolean);

        return formattedItems.length ? formattedItems.join(", ") : fallback;
    }

    return String(value);
};

const stripHtmlToText = (value, fallback = "") => {
    if (value === null || value === undefined || value === "") {
        return fallback;
    }

    const html = String(value);

    if (typeof DOMParser !== "undefined") {
        const doc = new DOMParser().parseFromString(html, "text/html");
        return (doc.body.textContent || "").trim() || fallback;
    }

    return html.replace(/<[^>]*>/g, "").trim() || fallback;
};

const resolveBranding = (_organization = {}) => {
    const primaryColor = REPORT_PRIMARY_COLOR;
    const stopColor = generateColor(primaryColor, 20);

    return {
        primaryColor,
        stopColor,
        sectionHeaderColor: primaryColor,
        textOnPrimary: REPORT_PRIMARY_TEXT_COLOR,
        bodyTextColor: REPORT_BODY_TEXT_COLOR,
        borderColor: REPORT_BORDER_COLOR,
        rowAltColor: REPORT_ROW_ALT_COLOR,
    };
};

const canUsePdfImageSrc = (src) => {
    if (!src || typeof src !== "string") {
        return false;
    }

    if (src.startsWith("data:")) {
        return true;
    }

    if (typeof window === "undefined") {
        return true;
    }

    try {
        const imageUrl = new URL(src, window.location.origin);

        return imageUrl.origin === window.location.origin;
    } catch (error) {
        return false;
    }
};

const resolveLogo = (organization = {}) => {
    const logo = {
        src: null,
        size: {},
    };

    if (organization?.secondaryLogo) {
        logo.src = organization.secondaryLogo;
        logo.size = {
            maxWidth: "200px",
            height: `${organization.secondaryLogoSize || 40}px`,
        };
    } else if (organization?.primaryLogo) {
        logo.src = organization.primaryLogo;
        logo.size = {
            maxWidth: "240px",
            maxHeight: "120px",
            height: `${organization.primaryLogoSize || 40}px`,
        };
    }

    if (!canUsePdfImageSrc(logo.src)) {
        logo.src = null;
    }

    return logo;
};

const formatPersonName = (person = {}) => {
    const firstName = person?.firsName || person?.firstName || person?.first_name || "";
    const lastName = person?.lastName || person?.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || person?.name || "";
};

const resolveCurrentUser = (user) => user?.user || user;

/** Corner radii so cell backgrounds stay inside a rounded table border. */
const roundedCornerStyle = ({
    isFirstRow = false,
    isLastRow = false,
    isFirstCol = false,
    isLastCol = false,
    radius = 5,
} = {}) => ({
    ...(isFirstRow && isFirstCol ? { borderTopLeftRadius: radius } : {}),
    ...(isFirstRow && isLastCol ? { borderTopRightRadius: radius } : {}),
    ...(isLastRow && isFirstCol ? { borderBottomLeftRadius: radius } : {}),
    ...(isLastRow && isLastCol ? { borderBottomRightRadius: radius } : {}),
    ...((isFirstRow || isLastRow) && (isFirstCol || isLastCol) ? { overflow: "hidden" } : {}),
});

const formatLinkedLegalEntity = (supplierDetail = {}) => {
    if (!supplierDetail) {
        return null;
    }

    const directValue =
        supplierDetail.linkedLegalEntityName
        || supplierDetail.linkedLegalEntity
        || supplierDetail.connectedLegalEntity;

    if (typeof directValue === "string") {
        return directValue;
    }

    if (directValue?.name) {
        return directValue.name;
    }

    const entities = supplierDetail.connectedLegalEntities
        || supplierDetail.linkedLegalEntities;

    if (Array.isArray(entities) && entities.length) {
        return entities
            .map((entity) => (typeof entity === "string" ? entity : entity?.name))
            .filter(Boolean)
            .join(", ");
    }

    return null;
};

export {
    DEFAULT_PRIMARY_COLOR,
    normalizeHexColor,
    generateColor,
    getContrastTextColor,
    formatDisplayValue,
    stripHtmlToText,
    resolveBranding,
    resolveLogo,
    formatPersonName,
    resolveCurrentUser,
    roundedCornerStyle,
    formatLinkedLegalEntity,
};